import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";

import { useSignedIn } from "../hooks/use-signed-in";
import { sitePath, useSiteConfig } from "../hooks/use-site-config";
import { BrandMark } from "./brand-mark";
import { BreathingRings } from "./breathing-rings";
import { GoLink } from "./go-link";
import { LandingMenu } from "./landing-menu";
import { ghostButton, smallButton } from "./section";
import { siteVars } from "./site-theme";
import { LanguageMenu } from "@/components/language-menu";
import { useShop } from "@/features/shop/hooks/use-shop";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { appConfig } from "@complete-athlete/enums";

/** The shop gets a link only once it has something on the shelf. */
const links = (slug: string | null, hasShop: boolean) => [
  { to: sitePath(slug, "services"), label: m.landing_nav_services() },
  { to: sitePath(slug, "coaches"), label: m.landing_nav_coaches() },
  { to: sitePath(slug, "pricing"), label: m.landing_nav_pricing() },
  ...(hasShop ? [{ to: sitePath(slug, "shop"), label: m.shop_title() }] : []),
  { to: sitePath(slug, "contact"), label: m.landing_nav_contact() },
];

const navLink =
  "shrink-0 rounded-full px-4 py-2 font-medium text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring";
const navLinkActive = { className: "text-foreground" };

/**
 * The name in the bar. On a trainer's page it is their business, with its logo when it has one,
 * so the visitor knows whose page this is before reading a word; the platform's own pages wear
 * the platform's mark.
 */
export const SiteBrand = ({ className }: { className?: string }) => {
  const { slug, brand } = useSiteConfig();

  if (brand) {
    return (
      <Link
        to={sitePath(slug)}
        className={cn(
          "group flex min-w-0 items-center gap-2.5 text-lg font-bold tracking-[-0.03em] outline-none focus-visible:ring-2 focus-visible:ring-ring",
          className,
        )}
      >
        {brand.logoUrl ? (
          <img
            src={brand.logoUrl}
            alt=""
            className="size-[30px] shrink-0 rounded-[10px] object-cover transition-transform duration-300 ease-out group-hover:-rotate-6 motion-reduce:transition-none"
          />
        ) : null}
        <span className="truncate">{brand.name}</span>
      </Link>
    );
  }

  return (
    <Link
      to={sitePath(slug)}
      className={cn("group flex items-center gap-2.5 text-lg font-extrabold tracking-[-0.04em] italic", className)}
    >
      <BrandMark className="transition-transform duration-300 ease-out group-hover:-rotate-6" />
      {appConfig.name}
    </Link>
  );
};

/** True once the page has moved off its top edge. */
const useScrolled = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const read = () => setScrolled(window.scrollY > 8);
    read();
    window.addEventListener("scroll", read, { passive: true });
    return () => window.removeEventListener("scroll", read);
  }, []);

  return scrolled;
};

/**
 * The bar every public page opens with. At the top of the page it has no surface of its own and
 * sits straight on the page; once the page scrolls it sticks and takes frosted glass behind it
 * (a near-solid card on phones, where blur is costly). On phones the links and the way in move into
 * the menu behind the round button.
 */
export const SiteNav = () => {
  const signedIn = useSignedIn();
  const scrolled = useScrolled();
  const { slug, organizationId } = useSiteConfig();
  const shop = useShop(organizationId);
  const navLinks = links(slug, shop.products.length > 0);

  return (
    <header
      data-scrolled={scrolled || undefined}
      className="sticky top-3 z-40 rounded-[28px] p-2.5 transition-[background-color,box-shadow,backdrop-filter] duration-200 data-scrolled:bg-card/95 md:data-scrolled:bg-card/60 md:data-scrolled:backdrop-blur-xl md:data-scrolled:backdrop-saturate-150 data-scrolled:shadow-[0_12px_32px_color-mix(in_oklch,var(--page-background)_70%,transparent)] md:rounded-full"
    >
      <div className="flex items-center gap-2 pl-2.5">
        <SiteBrand className="mr-auto md:mr-0" />
        <nav className="mx-auto hidden items-center gap-1 text-sm md:flex">
          {navLinks.map((link) => (
            <Link key={link.to} to={link.to} className={navLink} activeProps={navLinkActive}>
              {link.label}
            </Link>
          ))}
        </nav>
        <LanguageMenu style={siteVars} className="h-9 max-md:hidden" />
        {signedIn ? (
          <Link to="/my-bookings" className={cn(ghostButton, smallButton, "max-md:hidden")}>
            {m.landing_nav_my_bookings()}
          </Link>
        ) : (
          <Link to="/login" className={cn(ghostButton, smallButton, "max-md:hidden")}>
            {m.landing_nav_sign_in()}
          </Link>
        )}
        <GoLink to={sitePath(slug)} hash="times" size="small">
          {m.landing_nav_book()}
        </GoLink>
        <LandingMenu
          className="size-9 border-0 bg-[var(--site-well)] md:hidden"
          style={siteVars}
          brand={<SiteBrand />}
          links={navLinks}
          cta={
            <GoLink to={sitePath(slug)} hash="times">
              {m.landing_nav_book()}
            </GoLink>
          }
        />
      </div>
    </header>
  );
};

/**
 * What a sub-page opens with: the bar, then the page's title set straight on the page with the
 * landing's rings behind it. No card: the cards start with the page's own content. Anything
 * passed as children (a row of jump links, a filter) sits under the title.
 */
export const SiteHead = ({ children, className }: { children: ReactNode; className?: string }) => (
  <>
    <SiteNav />
    <div className={cn("relative isolate overflow-x-clip px-4 pb-9 md:px-6 md:pb-12", className)}>
      <BreathingRings className="-top-[150px] -right-[220px] size-[620px] [mask-image:linear-gradient(to_bottom,black_45%,transparent_92%)] opacity-50 md:-right-[90px] md:opacity-100" />
      {children}
    </div>
  </>
);

/** The title band sub-pages put inside `SiteHead`: one very large word, one line, one way to book. */
export const PageTitle = ({ title, aside }: { title: ReactNode; aside?: ReactNode }) => {
  const { slug } = useSiteConfig();

  return (
    <div className="pt-14 md:pt-[clamp(72px,14vh,150px)]">
      <h1 className="text-[clamp(3.5rem,11vw,10rem)] leading-[0.88] font-semibold tracking-[-0.06em]">{title}</h1>
      <div className="mt-7 flex flex-wrap items-center gap-x-8 gap-y-5 md:mt-9">
        <GoLink to={sitePath(slug)} hash="times">
          {m.landing_nav_book()}
        </GoLink>
        {aside ? <p className="max-w-[340px] text-[15px] text-muted-foreground">{aside}</p> : null}
      </div>
    </div>
  );
};
